import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/Icon';
import { api } from '@/lib/api';

/**
 * Чип-фильтр выбора ученика. Сам подгружает имя по `studentId`, если оно
 * пришло из URL без подсказки. По клику открывает дропдаун со списком и
 * полем поиска (через /api/students).
 *
 * Props:
 *   studentId        — текущее значение (number | '' | null)
 *   onChange(student) — student = { id, familyName, firstName, midName } | null
 */
export default function StudentFilterChip({ studentId, onChange }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const [rows, setRows]     = useState([]);
  const [loading, setLoad]  = useState(false);
  const [picked, setPicked] = useState(null); // запоминаем «полный» объект
  const ref = useRef(null);

  // Закрываем по клику вне
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  // Если studentId пришёл из URL, а имени нет — подгружаем
  useEffect(() => {
    if (!studentId) { setPicked(null); return; }
    if (picked && picked.id === Number(studentId)) return;
    let cancelled = false;
    api.get('/api/students/' + studentId)
      .then((res) => { if (!cancelled) setPicked(res.student || null); })
      .catch(() => { if (!cancelled) setPicked({ id: Number(studentId) }); });
    return () => { cancelled = true; };
  }, [studentId]);

  // Поиск с дебаунсом, активный пока меню открыто
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoad(true);
    const t = setTimeout(() => {
      const qs = new URLSearchParams({
        per_page: '300', status: 'active', sort: 'name', dir: 'asc',
      });
      if (search.trim()) qs.set('search', search.trim());
      api.get('/api/students?' + qs.toString())
        .then((res) => { if (!cancelled) setRows(res.rows || []); })
        .catch(() => { if (!cancelled) setRows([]); })
        .finally(() => { if (!cancelled) setLoad(false); });
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, search]);

  const isActive = !!studentId;
  const summary = (() => {
    if (!isActive) return null;
    if (!picked) return '#' + studentId;
    const fio = [picked.familyName, picked.firstName].filter(Boolean).join(' ').trim();
    return fio || ('#' + picked.id);
  })();

  return (
    <div className="chip-wrap" ref={ref}>
      <button
        type="button"
        className={'chip' + (isActive ? ' is-active' : '')}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="chip__label">Ученик</span>
        {isActive && <span className="chip__sep">:</span>}
        {isActive && (
          <span className="chip__value" style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {summary}
          </span>
        )}
        <Icon name="chevronDown" size={10} />
      </button>
      {open && (
        <div className="menu" style={{ minWidth: 320, padding: 6 }}>
          <input
            className="onec-picker__search"
            placeholder="Поиск по ФИО, телефону или родителю…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            style={{ marginBottom: 4 }}
          />
          <div style={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            {isActive && (
              <button
                type="button"
                className="menu__item"
                onClick={() => { onChange(null); setOpen(false); setSearch(''); }}
                style={{ color: 'var(--fg-muted)' }}
              >
                <span>Сбросить</span>
              </button>
            )}
            {loading && rows.length === 0 && (
              <div className="onec-picker__hint">Загрузка…</div>
            )}
            {!loading && rows.length === 0 && (
              <div className="onec-picker__hint">Никого не найдено</div>
            )}
            {rows.map((s) => {
              const fio = [s.familyName, s.firstName, s.midName].filter(Boolean).join(' ');
              const active = Number(studentId) === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  className={'menu__item' + (active ? ' is-active' : '')}
                  onClick={() => {
                    onChange(s);
                    setPicked(s);
                    setOpen(false);
                    setSearch('');
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {fio}
                    {s.class && <span className="muted" style={{ marginLeft: 8 }}>· {s.class.label}</span>}
                  </span>
                  {active && <Icon name="check" size={12} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
